/* eslint-disable prettier/prettier */
import BACKENDERROR from "app/static/frontend/imports/backenderror"

import { SubmissionError } from "redux-form/immutable"

import swal from "sweetalert"

import { segue, segueToUrl } from "global-action-creators"
import * as djangioActionCreators from "shared/djangio/action-creators"

import {
    getCampaignLinkPlaceholder,
    searchifyToSafedSearchFormat,
} from "app/static/frontend/outbound-texting/helpers"


import {
    getEstimatedCountActionDict,
    loadInitialRecipientsActionDict,
    postBulkSMSActionDict,
} from "outbound-texting/action-types"

import {
    swalConfigs as bulkSMSSwalConfigs,
    defaultSearchifySelectionObj,
    tableDisplayOptions,
} from "outbound-texting/constants"

// segue action creators
export const segueToOutboundTextingTable = (tableDisplayOption = tableDisplayOptions.sent) =>
    segue(`/texting/${tableDisplayOption.url}/`)

export const segueToOutboundTextingProfile = (id) => segue(`/texting/details/${id}/`)

export const segueToOutboundTextingCompose = (id) => segueToUrl(`/texting/compose/${id ? `${id}/` : ""}`)

const segueToBlankOutboundTextingCompose = () => segue(`/texting/compose/`)

// general API action creators
export const createBulkSMS = ({ kwargs = {}, payload = {} } = {}) =>
    djangioActionCreators.resourcePostList(
        DjangIO.app.texting.models.BulkSMS,
        postBulkSMSActionDict,
        {
            ...kwargs,
            current_status: DjangIO.app.texting.types.TextDeliveryStatus.draft.value,
        },
        payload
    )

/**
 * Given a campaign ID, makes a request to get the count
 * of the number of texting-eligible supporters who are
 * also relevant to the given campaign
 * Used to populate the estimated_count when creating a text
 * from a campaign
 *
 * @name getInitialEstimatedCountForCampaigns
 * @function
 * @param {Number} campaignId - the ID of a campaign to get supporter count for
 * @returns {Number}  when dispatched, returned promise result is the supporter count
 */
export const getInitialEstimatedCountForCampaigns = (campaignId) =>
    djangioActionCreators.resourceGetList(
        DjangIO.app.grassroots.models.Supporter,
        getEstimatedCountActionDict,
        {
            count_only: true,
            for_campaigns: [campaignId],
            for_texting: true,
        },
        {
            storeFields: ["forCampaignsContacts"],
        }
    )

// external action creators

/**
 * Creates a new BulkSMS from the supporters for a campaign (according to
 * the "for_campaigns" querymethod):
 * - Creates a new BulkSMS
 * - Segues to the texting compose form
 * - Pre-populates the searchify with an additional "for_campaigns" filter
 * - Pre-populates the description with the campaign's name
 * - Pre-populates the text section with a campaign link placeholder for this campaign
 *
 * Intended for use on pages besides the outbound texting form, such as
 * in the campaign profile action buttons
 *
 * @name createTextForCampaign
 * @function
 * @param {Number} campaignId - the ID of a campaign to create a text for
 * @param {String} campaignName - the name of the campaign, used to pre-fill the description
 * @returns {Object}  returns result of the last dispatch this function calls
 */
export const createTextForCampaign = (campaignId, campaignName) => async (dispatch) => {
    // Several parts of the form and the API rely on the estimated_count from searchify
    // It normally gets populated when the user opens the searchify, but since we are
    // pre-populating the searchify with the for_campaigns filter, the user may not open
    // the searchify before testing / sending the text
    // To make sure we have an estimated_count in case the user never opens searchify,
    // make an explicit request for the count here
    let resultCount = undefined
    try {
        resultCount = await dispatch(getInitialEstimatedCountForCampaigns(campaignId))
    } catch (error) {
        // if the request fails, return before sgeue or creating the BulkSMS
        return BACKENDERROR(error, bulkSMSSwalConfigs.somethingWentWrong)
    }

    // Post a new BulkSMS with the description, text, and selection already preset
    // with information from this campaign
    const response = await dispatch(
        createBulkSMS({
            kwargs: {
                description: `Text for Campaign: ${campaignName}`,
                text: getCampaignLinkPlaceholder(campaignId),
                recipient_selection: searchifyToSafedSearchFormat({
                    ...defaultSearchifySelectionObj,
                    filters: [
                        {
                            for_texting: true,
                            for_campaigns: [campaignId],
                        },
                    ],
                }),
                // make sure to include the recipient_count from the estimated count
                // we calculated in the previous request
                recipient_count: resultCount,
                create_safed_search: true,
            },
        })
    )

    // visit the compose page for the newly created text
    return dispatch(segueToOutboundTextingCompose(response.id))
}

// Load the supporters ID from database from the given filters
export const fetchInitialRecipients =
    (filters = []) =>
    (dispatch) => {
        return dispatch(
            djangioActionCreators.resourceGetList(
                DjangIO.app.grassroots.models.Supporter,
                loadInitialRecipientsActionDict,
                filters
            )
        )
    }

// Creates a new BulkSMS from supporters selection in safedSearchObj and redirect to compose page
export const createTextFromSafedSearch = (initialConfiguration, name = null) => async (dispatch) => {
    if (!initialConfiguration) return dispatch(segueToBlankOutboundTextingCompose())
    const { recipient_selection, ...configs } = initialConfiguration
    const args = {
        kwargs: {
            recipient_selection,
            recipient_count: recipient_selection.estimated_count,
            safed_search_label: name,
            create_safed_search: true,
            ...configs,
        },
    }

    const response = await dispatch(createBulkSMS(args))
    return dispatch(segueToOutboundTextingCompose(response.id))
}
